import { useEffect, useState } from 'react';
import { IconBrandWhatsapp, IconMail } from '@tabler/icons-react';
import {
  Button,
  Group,
  IconButton,
  Popover,
  Text,
} from '@wapharma/design-system';
import { isValidE164Phone, unwrapData } from '@wapharma/shared';
import { AsyncActionButton } from '../../components/AsyncActionButton';
import {
  StaffMessageComposer,
  type StaffContactChannel,
} from '../../components/StaffMessageComposer';
import {
  type StaffAccountCreator,
} from '../../components/StaffAccountDisplay';
import { type StaffSessionRow } from '../../components/StaffSessionsPanel';
import { type StaffReportingChain } from '../../components/StaffReportingGraph';
import { UserRolePill } from '../../components/UserRolePill';
import { apiGet } from '../../api';
import { type StaffActivityRow } from '../../staffActivityTimeline';

export const USER_CREATE_WIZARD_LABELS = [
  'Nom',
  'E-mail',
  'Photo',
  'Mobile',
  'Rôles',
  'Actif',
] as const;
export const USER_CREATE_STEP = {
  name: 0,
  email: 1,
  photo: 2,
  phone: 3,
  roles: 4,
  active: 5,
} as const;
export const USER_CREATE_LAST_STEP = USER_CREATE_WIZARD_LABELS.length - 1;

/** @deprecated Prefer USER_CREATE_STEP — kept for call-site aliases during migration. */
export const CREATE_USER_STEP = USER_CREATE_STEP;

export {
  staffCreateEmailConflictFromError,
  staffCreateEmailFormatError,
  staffCreateEmailIdentityError,
  staffEmailTakenInRows,
  staffFullNameDuplicateWarning,
} from './staffCreateEmailIdentity';

/** ASCII email-local token from a name part (accents stripped). */
export function staffEmailLocalToken(part: string): string {
  return part
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');
}

/** Suggested local-parts from prénom + nom (e.g. prenom.nom). */
export function staffEmailLocalSuggestions(firstName: string, lastName: string): string[] {
  const first = staffEmailLocalToken(firstName);
  const last = staffEmailLocalToken(lastName);
  const out: string[] = [];
  const push = (s: string) => {
    if (s && !out.includes(s)) out.push(s);
  };
  if (first && last) {
    push(`${first}.${last}`);
    push(`${first}${last}`);
    push(`${last}.${first}`);
    push(`${first[0]}.${last}`);
    push(`${first}.${last[0]}`);
  } else if (first) {
    push(first);
  } else if (last) {
    push(last);
  }
  return out;
}

export function getCreateUserPhoneFieldError(phone: string): string | undefined {
  const trimmed = phone.trim();
  if (!trimmed) return undefined;
  if (!isValidE164Phone(trimmed)) {
    return 'Numéro invalide. Vérifiez l’indicatif et le numéro.';
  }
  return undefined;
}

export function getCreateUserRolesFieldError(role: string | null): string | undefined {
  if (!role) return 'Sélectionnez un rôle.';
  return undefined;
}
export const USER_DETAIL_TABS = ['summary', 'roles', 'activity', 'history'] as const;
export type UserDetailTab = (typeof USER_DETAIL_TABS)[number];
export type UserDetailSection = 'profile' | 'roles';

export type UserRow = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone?: string | null;
  profilePictureKey?: string | null;
  profilePictureUrl?: string | null;
  active: boolean;
  createdAt: string;
  roles: string[];
  deletedAt?: string | null;
  createdBy: StaffAccountCreator | null;
  lastConnectedAt: string | null;
  lastSeenAt: string | null;
  isConnected: boolean;
  contactChannels?: StaffContactChannel[];
};

export type UserDetail = UserRow & {
  firstLoginAt?: string | null;
  mustChangePassword?: boolean;
  sessions?: StaffSessionRow[];
  lastSession?: {
    lastSeenAt: string | null;
    ip: string | null;
    userAgent: string | null;
    deviceType?: string | null;
    deviceLabel?: string | null;
    userAgentSummary?: string | null;
    locationLabel: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
  lastStaffActivity?: StaffActivityRow | null;
  reportingChain?: StaffReportingChain | null;
  phone?: string | null;
  contactChannels?: StaffContactChannel[];
};

export function RowMessagePopover({
  userId,
  token,
  channels,
  initialChannel,
  lockChannel,
  disabled,
  onSent,
  kind,
  triggerDisabled,
  triggerDisabledReason,
  messagesPath,
  whatsappExistsPath,
  bodyPlaceholder,
}: {
  userId: string;
  token: string | null;
  channels: StaffContactChannel[];
  initialChannel: 'EMAIL' | 'WHATSAPP';
  lockChannel: boolean;
  disabled?: boolean;
  onSent?: () => void;
  kind: 'email' | 'whatsapp';
  triggerDisabled?: boolean;
  triggerDisabledReason?: string;
  messagesPath?: string;
  whatsappExistsPath?: string;
  bodyPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [waCheck, setWaCheck] = useState<'idle' | 'loading' | 'yes' | 'no' | 'error'>('idle');

  useEffect(() => {
    if (!open) return;
    if (kind !== 'whatsapp') return;
    if (waCheck !== 'idle') return;
    if (!token) return;
    setWaCheck('loading');
    void apiGet<{ data: { exists: boolean } }>(
      whatsappExistsPath ?? `/admin/users/${userId}/whatsapp-exists`,
      token,
    )
      .then((r) => {
        const exists = Boolean((unwrapData(r) as { exists?: boolean })?.exists);
        setWaCheck(exists ? 'yes' : 'no');
      })
      .catch(() => setWaCheck('error'));
  }, [open, kind, waCheck, token, userId, whatsappExistsPath]);

  const waBlocked = kind === 'whatsapp' && (waCheck === 'loading' || waCheck === 'no' || waCheck === 'error');

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      portal
      align="end"
      portalMinWidth={320}
      portalMaxWidth={380}
      portalEstimatedHeight={340}
      target={
        <IconButton
          type="button"
          size="xs"
          variant="ghost"
          aria-label="Envoyer un message"
          aria-expanded={open}
          disabled={Boolean(triggerDisabled)}
          title={triggerDisabled ? triggerDisabledReason : undefined}
        >
          {kind === 'email' ? <IconMail size={16} stroke={1.8} /> : <IconBrandWhatsapp size={16} stroke={1.8} />}
        </IconButton>
      }
    >
      <div style={{ padding: 12, minWidth: 300 }}>
        {kind === 'whatsapp' && waCheck === 'loading' ? (
          <Text size="xs" c="dimmed" style={{ marginBottom: 8 }}>
            Vérification WhatsApp…
          </Text>
        ) : null}
        {kind === 'whatsapp' && waCheck === 'no' ? (
          <Text size="xs" c="dimmed" style={{ marginBottom: 8 }}>
            Numéro non enregistré sur WhatsApp.
          </Text>
        ) : null}
        {kind === 'whatsapp' && waCheck === 'error' ? (
          <Text size="xs" c="dimmed" style={{ marginBottom: 8 }}>
            Vérification WhatsApp indisponible.
          </Text>
        ) : null}
        <StaffMessageComposer
          userId={userId}
          token={token}
          disabled={disabled || waBlocked}
          contactChannels={channels}
          initialChannel={initialChannel}
          lockChannel={lockChannel}
          messagesPath={messagesPath}
          bodyPlaceholder={bodyPlaceholder}
          onSent={() => {
            setOpen(false);
            onSent?.();
          }}
        />
      </div>
    </Popover>
  );
}

export function userSectionEditActions(onCancel: () => void, onSave: () => Promise<void>) {
  return (
    <>
      <Button type="button" variant="ghost" onClick={onCancel}>
        Annuler
      </Button>
      <AsyncActionButton type="button" variant="primary" onAction={onSave} pendingLabel="Enregistrement…">
        Enregistrer
      </AsyncActionButton>
    </>
  );
}

export function formatUserDisplayTitle(detail: Pick<UserRow, 'firstName' | 'lastName' | 'email'>): string {
  return formatUserFullName(detail) ?? detail.email;
}

export function formatUserFullName(r: Pick<UserRow, 'firstName' | 'lastName'>): string | null {
  const first = r.firstName?.trim() ?? '';
  const last = r.lastName?.trim() ?? '';
  const full = [first, last].filter(Boolean).join(' ');
  return full || null;
}

export function UserRoleBadges({ roles }: { roles: string[] }) {
  if (!roles.length) {
    return (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }
  const sorted = [...roles].sort((a, b) => a.localeCompare(b, 'fr'));
  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0, maxWidth: '100%' }}>
      {sorted.map((code) => (
        <UserRolePill key={code} role={code} />
      ))}
    </Group>
  );
}
